import { Component } from '@theme/component';

/** Prototype's 2.5x side pane. The PDP still owns selection and the full-screen viewer. */
class AtelierGallery extends Component {
  #listeners; #observer; #resizeObserver; #root; #frame; #info; #pane; #lens; #view; #point;
  connectedCallback() {
    super.connectedCallback();
    this.#release();
    this.#root = this.closest('[data-atelier-a]');
    this.#frame = this.closest('.fp-commerce-gallery')?.querySelector('.fp-bleed');
    this.#info = this.#root?.querySelector('.fp-atelier-info');
    if (!this.#frame || !this.#info) return;
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#pane = document.createElement('div');
    this.#pane.className = 'fp-atelier-zoom-pane';
    this.#pane.dataset.atelierZoomPane = '';
    this.#pane.setAttribute('aria-hidden', 'true');
    this.#pane.hidden = true;
    this.#view = document.createElement('div');
    this.#view.dataset.atelierZoomImage = '';
    const tag = document.createElement('span');
    tag.textContent = '2.5×';
    this.#pane.append(this.#view, tag);
    this.#lens = document.createElement('div');
    this.#lens.className = 'fp-atelier-zoom-lens';
    this.#lens.setAttribute('aria-hidden', 'true');
    this.#lens.hidden = true;
    document.body.append(this.#pane, this.#lens);
    this.#frame.addEventListener('pointermove', event => {
      if (!matchMedia('(hover:hover) and (pointer:fine) and (min-width:1024px)').matches) return;
      this.#point = { x: event.clientX, y: event.clientY };
      this.#draw();
    }, options);
    this.#frame.addEventListener('pointerleave', this.#close, options);
    this.#frame.addEventListener('click', this.#close, options);
    document.addEventListener('keydown', event => { if (event.key === 'Escape') this.#close(); }, options);
    window.addEventListener('resize', this.#close, options);
    // The theme scrolls inside .page-wrapper; scroll events do not bubble to window.
    document.addEventListener('scroll', this.#close, { ...options, passive: true, capture: true });
    // Opening the advisor changes column sizes without a viewport resize.
    this.#resizeObserver = new ResizeObserver(this.#close);
    this.#resizeObserver.observe(this.#frame);
    this.#resizeObserver.observe(this.#info);
    this.#observer = new MutationObserver(this.#sync);
    this.#observer.observe(this.#root, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
    this.#sync();
  }
  #sync = () => {
    const thumbs = [...this.#root.querySelectorAll('[data-fp-rail] [data-fp-thumb]')];
    const active = thumbs.find(thumb => thumb.getAttribute('aria-pressed') === 'true') || thumbs[0];
    const index = active ? Number(active.dataset.fpThumb) : 0;
    const label = this.#frame.querySelector('[data-atelier-image-caption]');
    const count = this.querySelector('[data-atelier-photo-count]');
    if (label) {
      label.textContent = active?.dataset.imageLabel || '';
      label.hidden = !label.textContent;
    }
    if (count) count.textContent = `${index + 1} / ${thumbs.length}`;
    if (this.#point) this.#draw();
  };
  #draw() {
    const index = Number(this.#root.querySelector('[data-fp-rail] [aria-pressed="true"]')?.dataset.fpThumb || 0);
    const img = this.#frame.querySelectorAll('.fp-heroreel__img')[index];
    if (!img) return;
    const box = this.#frame.getBoundingClientRect(), info = this.#info.getBoundingClientRect();
    if (!box.width || !box.height || !info.width) return;
    const header = document.querySelector('header');
    const pinned = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position);
    const headBottom = pinned ? Math.max(0, header.getBoundingClientRect().bottom) + 12 : 12;
    let top = Math.max(box.top, headBottom);
    let height = Math.max(520, Math.min(box.bottom, innerHeight - 12) - top);
    if (top + height > innerHeight - 12) top = Math.max(headBottom, innerHeight - 12 - height);
    height = Math.min(height, innerHeight - 12 - top);
    Object.assign(this.#pane.style, { left: `${info.left}px`, top: `${top}px`, width: `${info.width}px`, height: `${height}px` });
    // Account for the cover crop so the lens and pane describe the same image pixels.
    const ratio = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
    let width = box.width, imageHeight = box.height;
    if (Number.isFinite(ratio) && ratio > 0) {
      if (box.width / box.height > ratio) imageHeight = box.width / ratio;
      else width = box.height * ratio;
    }
    const left = box.left + (box.width - width) / 2, imageTop = box.top + (box.height - imageHeight) / 2;
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const x = clamp(this.#point.x, box.left, box.right) - left;
    const y = clamp(this.#point.y, box.top, box.bottom) - imageTop;
    const zoom = 2.5, scaledWidth = width * zoom, scaledHeight = imageHeight * zoom;
    const offsetX = clamp(info.width / 2 - x * zoom, Math.min(0, info.width - scaledWidth), 0);
    const offsetY = clamp(height / 2 - y * zoom, Math.min(0, height - scaledHeight), 0);
    this.#view.style.backgroundImage = `url(${JSON.stringify(img.src)})`;
    this.#view.style.backgroundSize = `${scaledWidth}px ${scaledHeight}px`;
    this.#view.style.backgroundPosition = `${offsetX}px ${offsetY}px`;
    const lensWidth = Math.min(info.width / zoom, box.width), lensHeight = Math.min(height / zoom, box.height);
    Object.assign(this.#lens.style, {
      left: `${clamp(left - offsetX / zoom, box.left, box.right - lensWidth)}px`,
      top: `${clamp(imageTop - offsetY / zoom, box.top, box.bottom - lensHeight)}px`,
      width: `${lensWidth}px`, height: `${lensHeight}px`
    });
    this.#pane.hidden = false;
    this.#lens.hidden = false;
  }
  #close = () => {
    this.#point = null;
    if (this.#pane) this.#pane.hidden = true;
    if (this.#lens) this.#lens.hidden = true;
  };
  #release() {
    this.#listeners?.abort(); this.#observer?.disconnect(); this.#resizeObserver?.disconnect();
    this.#pane?.remove(); this.#lens?.remove(); this.#point = null;
  }
  disconnectedCallback() { super.disconnectedCallback(); this.#release(); }
}
if (!customElements.get('fivepoint-atelier-gallery')) customElements.define('fivepoint-atelier-gallery', AtelierGallery);
